import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { X, Loader2, Wallet, ShieldCheck, Gift } from "lucide-react";

const QUICK = [100, 500, 1000];

function loadCashfree() {
  return new Promise((resolve) => {
    if (window.Cashfree) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function DepositModal({ onClose, onSuccess, notice }) {
  const [amount, setAmount] = useState(notice ? 100 : 500);
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [cfMode, setCfMode] = useState("sandbox");

  useEffect(() => {
    api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
    api.get("/wallet/config").then(({ data }) => setCfMode(data.cashfree_mode || "sandbox")).catch(() => {});
  }, []);
  const balance = wallet?.deposit_balance ?? null;
  const bonusPct = wallet?.first_deposit_bonus_pct ?? 15;
  const bonusEligible = wallet ? !wallet.first_deposit_bonus_claimed : false;

  const finalAmount = custom ? parseInt(custom, 10) : amount;

  const pay = async () => {
    if (!finalAmount || finalAmount < 100) { toast.error("Minimum deposit is ₹100"); return; }
    setLoading(true);
    try {
      const ok = await loadCashfree();
      if (!ok) { toast.error("Could not load payment gateway"); setLoading(false); return; }
      const { data } = await api.post("/wallet/deposit/create-order", { amount: finalAmount });
      const cashfree = window.Cashfree({ mode: cfMode });
      const result = await cashfree.checkout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: "_modal",
      });
      if (result?.error) {
        toast.error(result.error.message || "Payment failed");
        setLoading(false);
        return;
      }
      // Modal closed — always verify server-side before crediting.
      const { data: v } = await api.post("/wallet/deposit/verify", { order_id: data.order_id });
      if (!v.ok) { toast.error("Payment not completed"); setLoading(false); return; }
      toast.success(v.bonus_credited > 0
        ? `Deposit successful! +₹${v.bonus_credited} first-deposit bonus. Balance: ₹${v.deposit_balance}`
        : `Deposit successful! Balance: ₹${v.deposit_balance}`);
      onSuccess?.(v);
      onClose?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(formatApiErrorDetail(d) || "Could not start payment");
    }
    setLoading(false);
  };

  return (
    <div className="absolute inset-0 z-[70] flex flex-col justify-end" data-testid="deposit-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !loading && onClose?.()} />
      <div className="relative bg-[#15121E] border-t border-white/10 rounded-t-3xl p-6 pb-8 animate-float-up max-h-[88%] overflow-y-auto no-scrollbar">
        <div className="flex items-center gap-2 mb-1">
          <Wallet className="w-5 h-5 text-rose-400" />
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Add money</h3>
          <button onClick={() => !loading && onClose?.()} className="ml-auto w-8 h-8 grid place-items-center text-slate-400 rounded-full active:bg-white/10"><X className="w-5 h-5" /></button>
        </div>
        {notice && (
          <div data-testid="deposit-notice" className="mb-4 p-3 rounded-xl bg-rose-500/10 border border-rose-400/30 text-rose-200 text-sm font-medium">{notice}</div>
        )}
        {balance != null && (
          <p className="text-sm text-slate-400 mb-4">Current deposit balance: <span className="text-white font-bold">₹{balance}</span></p>
        )}
        {bonusEligible && (
          <div data-testid="first-deposit-bonus-banner" className="mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-400/30 flex items-start gap-2.5">
            <Gift className="w-4.5 h-4.5 text-emerald-400 shrink-0 mt-0.5" />
            <p className="text-[13px] text-emerald-200 leading-snug">
              <span className="font-bold">First deposit bonus:</span> get +{bonusPct}% extra from GulMatch on your first deposit
              {finalAmount >= 100 ? <> — deposit ₹{finalAmount}, receive <span className="font-bold">₹{(finalAmount * (1 + bonusPct / 100)).toFixed(0)}</span></> : ""}.
              One-time offer.
            </p>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 mb-3">
          {QUICK.map((q) => (
            <button key={q} data-testid={`deposit-quick-${q}`} onClick={() => { setAmount(q); setCustom(""); }}
              className={`h-12 rounded-xl text-sm font-bold border transition-colors ${!custom && amount === q ? "bg-rose-500 border-rose-500 text-white" : "bg-white/5 border-white/10 text-slate-300"}`}>
              ₹{q}
            </button>
          ))}
        </div>
        <input data-testid="deposit-custom-input" type="number" inputMode="numeric" value={custom}
          onChange={(e) => setCustom(e.target.value)} placeholder="Or enter a custom amount (₹)"
          className="w-full h-12 px-4 mb-5 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:outline-none focus:border-rose-500/60" />

        <button data-testid="deposit-pay-button" onClick={pay} disabled={loading}
          className="w-full h-12 rounded-2xl bg-gradient-to-r from-[#FF2E63] to-[#FF6B8B] text-white font-bold active:scale-[0.98] transition-transform disabled:opacity-60 flex items-center justify-center gap-2">
          {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Opening payment…</> : `Pay ₹${finalAmount || 0} securely`}
        </button>
        <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 mt-3">
          <ShieldCheck className="w-3.5 h-3.5" /> Secured by Cashfree · UPI, cards & QR supported
        </p>
      </div>
    </div>
  );
}
